import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../login/AuthProvider";
import { add } from "../../components/apipath";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";

function AddRequestForm() {

    const { token, mem_id, mem_status, authorized } = useAuth();
    const navigate = useNavigate();
    const QTY_IDS = [13, 14];
    const MEMO_IDS = [15, 20, 21];
    const ADDON_GROUPS = {
    CPA: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 19, 15, 20, 21, 17],
    CMA: [25, 26],
    EA: [22, 23, 24],
    其他: [13, 14, 18],
    };
    const DOMESTIC_BOOK_IDS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 25, 26];
    const OVERSEAS_BOOK_IDS = [22, 23, 24];
    const BOOK_IDS = [...DOMESTIC_BOOK_IDS, ...OVERSEAS_BOOK_IDS];
    const isBookAddon = (items = []) => {
    if (!Array.isArray(items)) return false;
    // 若是物件陣列
    if (items.length && typeof items[0] === 'object') {
        return items.some(i => BOOK_IDS.includes(Number(i.id)));
    }
    // 若是 id 陣列
    return items.some(i => BOOK_IDS.includes(Number(i)));
    };

    const getShippingInfo = (
        selectedItems,
        quantityMap = {},
        addressType = "domestic",
        deliver = "no"
    ) => {
        if (deliver !== "yes") {
            return {
                domesticCount: 0,
                overseasCount: 0,
                domesticBoxes: 0,
                overseasBoxes: 0,
                totalBoxes: 0,
                feePerBox: 0,
                shippingFee: 0,
            };
        }

        let domesticCount = 0;
        let overseasCount = 0;

        selectedItems.forEach((item) => {
            const id = Number(item.id);
            const qty = Number(quantityMap[id] || 1);

            if (DOMESTIC_BOOK_IDS.includes(id)) domesticCount += qty;
            if (OVERSEAS_BOOK_IDS.includes(id)) overseasCount += qty;
        });

        const domesticBoxes = Math.ceil(domesticCount / 2);
        const overseasBoxes = Math.ceil(overseasCount / 4);
        const totalBoxes = domesticBoxes + overseasBoxes;
        const feePerBox = addressType === "foreign" ? 1200 : 100;
        const shippingFee = totalBoxes * feePerBox;

        return {
            domesticCount,
            overseasCount,
            domesticBoxes,
            overseasBoxes,
            totalBoxes,
            feePerBox,
            shippingFee,
        };
    };

    useEffect(() => {
        if (!authorized || String(mem_status) !== "1") {
            Swal.fire({
                icon: 'warning',
                title: '學員專屬功能',
                text: '此頁面僅限學員使用，請先完成購買並登入',
                confirmButtonText: '了解',
                confirmButtonColor: '#224040',
            }).then(() => {
                navigate('/member/memberlogin');
            });
        }
    }, []);
    
    const [formData, setFormData] = useState({
        mem_id: mem_id,
        deliver: "no",
        address: "",
        address_type: "domestic",
        invoice: "personal",
        company_name: "",
        company_tax: "",
        remit_time: "",
        remit_name: "",
        status: "審核中",
        total_amount: 0,
        add_on_items: [],
        quantityMap: {},
        memoMap: {},
    });
    
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [agreementChecked, setAgreementChecked] = useState({
        reviewedTerms: false,
        understandPolicy: false,
        acceptTerms: false,
    });

    const isAgreementValid = () => {
        return agreementChecked.reviewedTerms && agreementChecked.understandPolicy && agreementChecked.acceptTerms;
    };
    
    const handleAgreementChange = (e) => {
        const { name, value } = e.target;
        setAgreementChecked(prev => ({
            ...prev,
            [name]: value === "yes",
        }));
    };

    const [addOnOptions, setAddOnOptions] = useState([]);
    
    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const authData = JSON.parse(localStorage.getItem("auth"));
                if (authData && authData.chinese_name) {
                    setFormData((prevData) => ({
                        ...prevData,
                        studentName: authData.chinese_name,
                        account: authData.account,
                    }));
                }
            } catch (error) {
                console.error("無法解析 auth 資料", error);
            }
    
            try {
                const response = await axios.get(`${add}addon_items`);
                setAddOnOptions(response.data);
            } catch (err) {
                console.error("載入加購項目錯誤", err);
            }
        };
    
        fetchInitialData();
    }, []);

    // 計算總金額
    const calculateTotal = (
        selectedItems,
        quantityMapArg = formData.quantityMap,
        addressTypeArg = formData.address_type,
        deliverArg = formData.deliver
    ) => {
        const itemTotal = selectedItems.reduce((sum, item) => {
            const qty = Number(quantityMapArg[item.id] || 1);
            return sum + Number(item.price) * qty;
        }, 0);

        const shippingInfo = getShippingInfo(
            selectedItems,
            quantityMapArg,
            addressTypeArg,
            deliverArg
        );

        setFormData(prev => ({
            ...prev,
            total_amount: itemTotal + shippingInfo.shippingFee,
        }));
    };

    const shippingInfo = getShippingInfo(
        formData.add_on_items,
        formData.quantityMap,
        formData.address_type,
        formData.deliver
    );

    // 更新表單欄位
    const handleChange = (e) => {
        const { name, value } = e.target;

        const newFormData = {
            ...formData,
            [name]: value
        };

        setFormData(newFormData);

        if (["deliver", "address_type"].includes(name)) {
            calculateTotal(
                newFormData.add_on_items,
                newFormData.quantityMap,
                newFormData.address_type,
                newFormData.deliver
            );
        }
    };

    // 處理加購項目變更
    const handleCheckboxChange = (e) => {
        const { value, checked } = e.target;
        const selectedId = parseInt(value, 10);

        const item = addOnOptions.find(i => Number(i.id) === selectedId);
        if (!item) return;

        setFormData(prev => {
            let updatedItems = [...prev.add_on_items];
            let newQuantityMap = { ...prev.quantityMap };
            let newMemoMap = { ...prev.memoMap };

            if (checked) {
                if (!updatedItems.some(i => Number(i.id) === selectedId)) {
                    updatedItems.push(item);
                }

                if (QTY_IDS.includes(selectedId) && !newQuantityMap[selectedId]) {
                    newQuantityMap[selectedId] = 1;
                }

                if (MEMO_IDS.includes(selectedId) && newMemoMap[selectedId] === undefined) {
                    newMemoMap[selectedId] = "";
                }
            } else {
                updatedItems = updatedItems.filter(i => Number(i.id) !== selectedId);
                delete newQuantityMap[selectedId];
                delete newMemoMap[selectedId];
            }

            const itemTotal = updatedItems.reduce((sum, item) => {
                const qty = Number(newQuantityMap[item.id] || 1);
                return sum + Number(item.price) * qty;
            }, 0);

            const shippingInfo = getShippingInfo(
                updatedItems,
                newQuantityMap,
                prev.address_type,
                prev.deliver
            );

            return {
                ...prev,
                add_on_items: updatedItems,
                quantityMap: newQuantityMap,
                memoMap: newMemoMap,
                total_amount: itemTotal + shippingInfo.shippingFee,
            };
        });
    };
    
    const handleSubmit = async (e) => {
    e.preventDefault();
    
    const missingMemo = formData.add_on_items
    .map(i => Number(i.id))
    .filter(id => MEMO_IDS.includes(id))
    .some(id => !(formData.memoMap[id] || "").trim());

    if (missingMemo) {
    Swal.fire({
        icon: "warning",
        title: "請填寫備註",
        text: "您勾選了需要備註的加購項目，請先填寫備註再送出。",
        confirmButtonText: "確認",
    });

    setIsSubmitting(false);
    return;
    }
    // 如果沒有勾選所有同意選項，則顯示錯誤
    if (!isAgreementValid()) {
        Swal.fire({
            icon: "warning",
            title: "請確認所有條款",
            text: "請確保您已勾選所有「同意與閱讀」選項後再提交。",
            confirmButtonText: "確認",
        });
        return;
    }

    setIsSubmitting(true);

    try {
        const dataToSend = {
            ...formData,
            add_on_items: formData.add_on_items.map(item => item.id).join(","),
            memo: formData.add_on_items
            .map(item => {
                const id = Number(item.id);
                const qty = formData.quantityMap[id] || 1;
                const note = (formData.memoMap[id] || "").trim();

                if (QTY_IDS.includes(id)) return `${item.name} x${qty}`;

                if (MEMO_IDS.includes(id)) return note;

                return item.name;
            })
            .filter(Boolean)
            .join(" | ")
        };

        const response = await axios.post(`${add}`, dataToSend, { headers: { token } });

        if (response.status === 200) {
            Swal.fire({
                icon: "success",
                title: "加購申請成功",
                text: "請等待審核！",
                confirmButtonText: "確認",
            });
            if (isBookAddon(formData.add_on_items)) {
            
                const result = await Swal.fire({
                icon: "question",
                title: "需要現在申請領書時間嗎？",
                text: "您已加購書本，可立即前往選擇領書時間與方式。",
                showCancelButton: true,
                confirmButtonText: "前往領書申請",
                cancelButtonText: "稍後再說",
                confirmButtonColor: "#224040",
            });
            if (result.isConfirmed) {
                navigate("/member/book");
                return;
            }
            }
            navigate("/member");
            }
            } catch (error) {
                Swal.fire({
                    icon: "error",
                    title: "提交失敗",
                    text: error.response?.data?.message || "請稍後再試",
                    confirmButtonText: "確認",
                });
            }

            setIsSubmitting(false);
        };
        const groupedAddOnOptions = Object.entries(ADDON_GROUPS).map(([groupName, ids]) => ({
            groupName,
            items: ids
                .map(id => addOnOptions.find(item => Number(item.id) === id))
                .filter(Boolean)
        }));
    return (
        <div className="container mb100 mt100">
            <h2 className="font-weight-bold pb-5 text-center">Becker CPA/CMA/EA (美國註冊會計師) 學員加購_線上報名表</h2>
            <div className="card w-75 p-5 m-auto">
                <div className="card-body">
                    
                敬請完整閱讀此份報名表單，確實了解課程相關規定，包含退費、上課時數、軟體效期等權益與規定後，再行填寫此份線上報名表與完成繳費。
                        <div className="mt-5">
                        Becker 系列產品購買須知與相關規定  (請務必詳閱)
                        1. 購買方案者，須填妥本訂購單資料表，完整審閱同意事項後，完成付費，方符合訂購程序。繳費前，請先確認購買產品內容與費用。
                            <ul>
                                <li>購買前請確認完整審閱此份購買須知與相關規定，審閱期已達五日。</li>
                                <li>本商品屬於行政院公告之「通訊交易解除權合理例外情事適用準則」第2條可排除消費者保護法第19條七日解除權之合理例外情事者，惟若商品未經領取、未開始參加課程、且未登入任何產品內含學習帳號時，購買後七日猶豫期內得要求退費（若已取得諮詢者，退費時需扣除每階段1,000元諮詢費）。</li>
                                <li>教材一經領取或帳號登入後，恕不退費亦不得要求暫停效期。</li>
                                <li>學員領取帳號後，應妥善保管其登入密碼，確保不為他人得知，利用學員帳號密碼登入學習帳號，將推定為學員本人之行為。因非可歸責於 Becker Taiwan (捷進)之事由導致登入密碼外洩時， 學員應儘速通知，Becker Taiwan (捷進)除提供必要協助外，不負任何法律上責任。</li>
                                <li>購買方案者保證為完全行為能力人或已得法定代理人同意之限制行為能力人。</li>
                                <li>使用本服務應具備下列之軟硬體設備基本規格及要求：
                                    <ul>
                                        <li>Windows PC/NB：Windows 7 以上</li>
                                        <li>Apple PC/NB：macOS 10 以上</li>
                                        <li>Apple 行動裝置：iOS 13.0 以上</li>
                                        <li>Android 行動裝置：Android 8.0 以上</li>
                                        ※注意事項：行動裝置限定使用APP版觀看課程<br/> 
                                        連線需求：
                                        <li>寬頻網路下載速度20Mbps以上</li>
                                        <li>不建議無線、社區型、有線電視台網路、公共免費網路，可能造成無法正常驗證登入或個人帳號密碼資料外洩。</li>
                                        ※注意事項：行動裝置限定使用APP版觀看課程。<br/>
                                        ※其餘未列示之軟硬體或系統，恕不保證其觀看學習品質
                                    </ul>
                                </li> 
                            </ul> 
                        2. 學員資料表 E-mail 信箱資訊，有效期限必須達二十四個月以上，購買後請自行定期收信，本軟體服務及一切相關資訊將以該 E-mail 信箱為聯繫方式，若因學員未定期收信導致權益受影響，學員應自負其責。<br/>
                        3. Becker CPA, CMA, EA Exam Review系列為美國進口原版教材與軟體，方案內含之教材、課程、諮詢服務皆限購買者個人使用，學員應遵循 Becker 服務政策，且嚴禁任何形式之轉售、轉贈、轉讓及分享，若經發現，學員應賠償購買總價10倍之懲罰性違約金及損害賠償。<br/>
                        <ul>
                            <li>使用權限效期：
                                <ul>
                                    <li>美國註冊會計師系列 Becker CPA Exam Review 與 Becker CPA Exam Final Review 使用權限效期於軟體購買當日起算後之二十四個月後終止。(Your Becker CPA Exam Review license will expire 24 months from the date of purchase.)</li>
                                    <li>美國管理會計師系列 Becker CMA Exam Review 使用權限效期於軟體購買當日起算後之二十四個月後終止。(Your Becker CMA Exam Review license will expire 24 months from the date of purchase.)</li>
                                    <li>美國註冊稅務師系列 Becker EA Exam Review 使用權限效期於軟體購買當日起算後之二十四個月後終止。(Your Becker EA Exam Review license will expire 24 months from the date of purchase.)</li>
                                </ul>
                            </li>
                            <li>2025年1月1日起，CPA, CMA 所有方案提供電子教科書，不包含紙本教科書。因個人筆記習慣須加購教科書者，每本需酌收加購費用，加購價格依美國 Becker 規定之定價加上國際運費。</li>
                            <li>所有方案內含之服務、課程、課本，需於效期內領取與使用。</li>
                            <li>購買 CPA 專業方案與全修方案、EA 完勝方案者，符合持續展延 (Unlimited Access) 使用條款(見第4項)。</li>
                            <li>為提升服務品質及確保學員正確使用本教材，Becker Professional Education與 Becker Taiwan (捷進) 將蒐集、處理並分析系列產品使用帳號之軟體登錄時間、網路位址 (IP address) 等相關資訊。在任何情況下，系統若發現有多人登錄同一帳號情況， Becker Taiwan (捷進) 有權中止該帳號之軟體權限。</li>
                            <li>遇 Becker 系列教科書版本更新、而須加購新版書本者，需於使用效期內，檢附「過去三個月內應考而成績未及格證明」或「即將赴考之考場預約 (Prometric appointment confirmation) 證明」做為購買憑證，每本書需酌收加購費用，加購價格依美國 Becker 規定之定價加上國際運費。</li>
                            <li>若遇書籍取用完畢，需等待國際郵務寄達台灣，無法指定取書時間。</li>
                        </ul>
                        4. 終止契約
                        學員如有違反強制禁止規定或公序良俗之行為、或違反本契約經限期改正仍未改正者， Becker Taiwan (捷進) 得終止本契約，不得異議。如學員自行或因可歸責於己之事由終止契約者，教材一經領取或帳號登入後，恕不退費。<br/>
                        5. 持續展延 (Unlimited Access) 條款<br/>
                        <ul>
                            <li>購買 Becker CPA「含」持續展延資格產品之學員，於使用效期內，可立即適用持續展延條款；未符合以上資格而僅購買 Becker CPA 「無」持續展延資格產品之學員，於使用效期內得以價差加購持續展延資格。</li>
                            <li>展延申請方式：
                                <ul>
                                    <li>CPA （美國註冊會計師）：
                                        <ul>
                                            <li>持有持續展延資格之 CPA 學員，每一次使用權限到期日前30日內，需提出學員本人「即將赴考某科目之考場預約 (Prometric appointment confirmation) 證明」，即得展延該科軟體效期六個月，無須額外付費。未能於到期日前提出申請並通過審核者，即喪失效期和持續展延申請資格。</li>
                                            <li>軟體效期若獲免費展延，延長之效期起算日為原有到期日之隔天，不得提早或延後起算。</li>
                                        </ul>
                                    </li>
                                    <li>EA（美國註冊稅務師）：
                                        <ul>
                                            <li>購買包含持續展延資格之方案者，於效期內得提供考場預約證明，申請展延單科 4 個月，並以原到期日翌日為起算日。需於期限前提出申請。 </li>
                                            <li>軟體效期若獲免費展延，延長之效期起算日為原有到期日之隔天，不得提早或延後起算。</li>
                                        </ul>
                                    </li>
                                </ul>
                            </li>
                        </ul>
                        6. Becker Taiwan (捷進) 中文課程產品內容說明與限制：<br/>
                        <ul>
                            <li>中文課程使用原則
                                <ul>
                                    <li>CPA（美國註冊會計師）：
                                        <ul>
                                            <li>
                                                學員購買套組含加值服務之各類型中文課程者，全科學員得免費使用之總時數額度為購買當年一期四科面授時數加上四十小時，非全科學員使用之總時數額度為單科面授時數加上十小時。
                                            </li>
                                            <li>
                                                專業、全修、選修方案內含之面授、或雲端中文課程，學員可以依所持時數額度，選擇以面授、或雲端型式使用，依逐次使用時數扣除額度，使用後不得要求變更、轉讓以及重複行使權益，首次登入軟體當日視為實際開課日。
                                            </li>
                                            <li>學員單科選擇面授型式，需於於軟體效期內選擇一期之該科面授課程預定教室座位名額，完整參加，單科不得要求分期上課；面授一堂為三小時，乘上該科堂數由學員使用時數額度中扣除。</li>
                                        </ul>
                                    </li>
                                    <li>EA（美國註冊稅務師）：
                                        <ul>
                                            <li>
                                                中文課程使用原則（適用含中文課程的方案）：
                                                <ul>
                                                    <li>中文課程包含面授與雲端形式，授課時數依每期官網公告課表公告顯示為準。</li>
                                                    <li>課程僅限於考試通過前使用，並須於軟體效期內完成。</li>
                                                    <li>面授課請假者，可申請補課，補課以雲端形式進行，恕不主動提供額外補課時數。</li>
                                                    <li>若曾參加課程但考試未通過，得於課程開課期間，檢附未通過成績單，申請該科中文課程重上一次。</li>
                                                </ul>
                                            </li>
                                            <li>
                                                中文課程加購與限制（適用未含中文課程方案）
                                                <ul>
                                                    <li>未含中文課程之方案購買者，可選擇 加購單堂中文課程（3 小時）NT$1,500 元。未滿 3 小時之單堂課程，以每小時 NT$500 元計。</li>
                                                    <li>加購者不得使用中文講師線上解題服務。</li>
                                                    <li>加購課程需依 Becker Taiwan 開課規劃安排，需提前預約。</li>
                                                </ul>
                                            </li>
                                        </ul>
                                    </li>
                                </ul>
                            </li>
                            <li>
                                全系列產品各方案可能包含之面授、或雲端課程、線上發問系統、顧問諮詢等在地中文加值服務之使用有效期限與軟體效期一致。
                                <ul>
                                    <li>學員軟體使用效期結束時，若未使用完畢加值服務之各類型課程時數，剩餘時數等同失效，不得異議。</li>
                                    <li>中文課程研發目的在幫助考生加速通過考試，故學員使用或參加中文課程時間點為通過考試之前。</li>
                                    <li>面授、或雲端課程中不得針對內容進行任何錄音、錄影等側錄行為。</li>
                                    <li>購買「無」中文課程產品者，不得要求索取中文加值服務之各項研習講義、文件資料。</li>
                                    <li>專業、全修、選修方案內含之面授、或雲端中文課程，學員可以依所持時數額度，選擇以面授、或雲端型式使用，依逐次使用時數扣除額度，使用後不得要求變更、轉讓以及重複行使權益，首次登入軟體當日視為實際開課日。</li>
                                    <li>學員單科選擇面授型式，需於於軟體效期內選擇一期之該科面授課程預定教室座位名額，完整參加，單科不得要求分期上課；面授一堂為三小時，乘上該科堂數由學員使用時數額度中扣除。</li>
                                    <li>全系列各方案可能內含之中文課程，提供有面授、或雲端上課型式，Becker Taiwan (捷進) 保有各上課形式之開課時間、現場空間開放時間、雲端影片開放時間之決定權，學員不得異議。</li>
                                    <li>使用面授課程者若有缺課情況，缺席課程以雲端內容進行補課，補課時數與面授缺席時數相同，不得要求增加時數。</li>
                                    <li>Becker Taiwan (捷進) 保有中文課程開課日期之決定權，依各期實際開課公告認定，學員不得異議。</li>
                                    <li>學員單科選擇雲端型式，可於軟體效期內隨時使用，每次申請並使用觀看點數為計算憑證，由學員使用時數額度中扣除。每一點數代表三小時上課時數。</li>
                                    <li>Becker Taiwan (捷進) 保有中文雲端課程於雲端提供時間點、提供內容範圍之決定權，依各期課程影片上架公告認定，學員不得異議。</li>
                                </ul>
                            </li>
                            <li>
                                學員購買方案含加值服務之各類型課程者，免費時數額度用罄情況，於軟體有效效期內，得以單堂三小時為單位加購使用額度，
                                <ul>
                                    <li>曾購買全科方案者，單堂加購金額為台幣一千五百元整。</li>
                                    <li>曾購買非全科方案者，單堂加購金額為台幣兩千五百元整。</li>
                                </ul>
                            </li>
                            <li>Becker Taiwan (捷進) 保有各項諮詢服務、教室座位、雲端流量分配之決定權，學員欲使用上開服務均須提前來電或來信預約，以確認實體辦公室與面授教室服務狀態與起迄時間。</li>
                            <li>配合中華民國各級政府單位規定，遇天災、疫情、緊急事件時獲所屬區域政府單位指示，Becker Taiwan (捷進) 將依指示暫時關閉實體教室場域，學員需待解除關閉後，參加面授課程，不得異議。</li>
                            <li>以上中文課程項目與內容，Becker Taiwan (捷進) 保留變更、終止、修改及解釋之權利。</li>
                        </ul>
                        7. Becker Taiwan (捷進) 提供之考程顧問諮詢服務，係指由開始準備至通過四科考試為止之報考流程顧問諮詢說明與文件總檢服務。 <br/>
                        <ul>
                            <li>CPA（美國註冊會計師）：
                                <ul>
                                    <li>針對購買專業與全科方案者，提供即時透過網路或電話進行之諮詢，另提供選擇性現場諮詢、每次至多30分鐘；為保障雙方權益，現場諮詢過程可能將全程錄音。</li>
                                    <li>針對購買選修方案者，提供三次諮詢，單次為20分鐘，各次諮詢範圍明確定義如下，學員不得要求超出此限外之諮詢時間與內容：
                                        <ul>
                                            <li>第一次諮詢內容：選州流程說明</li>
                                            <li>第二次諮詢內容：學歷認證流程說明</li>
                                            <li>第三次諮詢內容：報名考試與預約考場流程說明</li>
                                        </ul>
                                    </li>
                                </ul>
                            </li>
                            <li>EA（美國註冊稅務師）：
                                <ul>
                                    <li>顧問諮詢服務於 2025 年 8 月啟用，提供線上文字、影片講解。</li>
                                </ul>
                            </li>
                            <li>報考流程顧問諮詢說明與文件總檢服務為購買者(學員)本人之權益，為避免溝通有誤或疏漏，流程說明與對話皆僅提供予購買者本人。</li>
                        </ul>
                        8. 捷進顧問有限公司 (Becker Taiwan) 學分評估僅供參考不得視為保證，購買者之報考資格應以考試指定之認證機構以及報名審查官方機構出具之審查結果為準。<br/>
                        9. 賣方保留任何因賣方代理原廠停產因素而停止供貨的權利，賣方亦保留任何因賣方代理原廠更改貨品規格而更改之權利並對此不負擔責任。<br/>
                        10. 捷進顧問有限公司 (Becker Taiwan) 已依照台北市短期補習班管理規則之規定立案登記，並已加入中華民國補教業品保協會辦理之補習服務聯合連帶保證協定 (品保協會會員編號：020267)。<br/>
                        <ul>
                            <li>購買捷進顧問有限公司 (Becker Taiwan) 任一方案者，享有聯合服務連帶保證協定，如 Becker Taiwan (捷進) 因財務或其他原因等問題致其補習服務無法提供，學生得於地方主管機關認定其無法提供補習服務時起六個月內向加入本協定之性質相近之補習班 (以同一直轄市或縣市為原則) 換取等值之服務（但不包括原補習服務之贈品或贈送課程）；等值之服務除依一般方式授課外，得以多元之教學方式辦理。</li>
                            <li>隱私權聲明<br/>
                                捷進顧問有限公司感謝您訂購 Becker 教材方案。本公司將依個人資料保護法第8條第1項之要求，向您告知以下事項：<br/>
                                    (1) 蒐集目的：依照101年10月1日修正之個人資料保護法之特定目的及個人資料之類別，040行銷、069契約、類似契約或其他法律關係事務、090消費者客戶管理與服務、157調查、統計與研究分析、181其他經營合於營業登記項目或組織章程所定之業務182其他諮詢與顧問服務。<br/>
                                    (2) 個人資料類別： C001電子郵件、中文姓名、英文拼音全名、電話號碼。C002金融機構帳戶號碼與姓名。C003身分證字號。C036是否曾訂購本公司產品、得知本公司之管道。C038在職身分C051所屬或畢業學校與科系。<br/>
                                    (3) 個人資料利用之期間、地區：我們會在您使用本公司提供服務之期間與地區利用您的個人資料。<br/>
                                    (4) 個人資料利用對象及方法：本公司將會依照您所填寫之訂購資料，確認您已完成訂購，並替您註冊帳號、寄送商品、提供學員管理、行銷活動等服務。並將分析您提供之類別C036、C038、C051等資訊，以增進本公司之服務。在未經您的同意下，我們不會將您的個人資料提供給第三人或做不當利用。<br/>
                                    (5)如您所提供之個人資料不正確或不完整，可能將無法完整履行訂購單內容。<br/>
                                    (6)  您得行使個資法第3條所賦予之相關權利，如您欲行使該權利請來信 service@beckertw.com。
                            </li>
                        </ul>
                        
                        </div>
                    <div className="mt-5 mb-5">
                    捷進顧問有限公司 (Becker Taiwan) 附設短期補習班立案資訊<br/>
                    核准立案名稱全名：捷進顧問有限公司附設臺北市私立貝克商業文理短期補習班<br/>
                    核准字號：北市補習班證第7620號<br/>
                    負責人：陸秀麗<br/>
                    </div>
                    <div className="mt-2 mb-5">
                        <ul>
                            <li>聯絡電話：02-2700-0053 </li>
                            <li>地址：臺北市大安區住安里信義路四段6號16樓之12</li>
                            <li>傳真：02-2700-0616</li>
                            <li>電子郵件：service@beckertw.com</li>
                        </ul>
                    </div>
                    
                    <div className="mt-5 mb-5">
                    付費購買匯款資訊<br/>
                    【在台購買】<br/>
                    收款人戶名：捷進顧問有限公司<br/>
                    受款銀行：華南商業銀行信維分行 (代碼：008 )<br/>
                    收款人帳號：149-10-010062-0<br/>

                    匯款金額：以新台幣付費，依各方案價格 (若需快遞書籍，請加上運費)<br/>

                    台灣境內快遞計費方式：便利袋快遞費每箱新台幣 100 元。因快遞公司重量限制，寄送 1-2 本為一箱，寄送 3-4 本須為兩箱。<br/>
                    __________________<br/>

                    【海外購買】<br/>
                    Recipient Name: Direct Line Consulting Co.<br/>
                    Account Number: 149-10-010062-0<br/>

                    Bank: Hua Nan Commercial Bank Ltd.<br/>
                    Branch: Hsin-Wei Branch<br/>
                    SWIFT Code (Bank ID): HNBKTWTP149<br/>
                    Bank Address: 2F, No.6, Xinyi Rd. Sec. 4, Da'an Dist., 106 Taipei City, Taiwan<br/>

                    匯款金額：以新台幣付費，依各方案價格 (若需快遞書籍，請加上運費，全額到匯，計算方式請向您的支付銀行確認，若無法估算，請直接另附新台幣200元之中轉行手續費)<br/>

                    快遞計費方式： 由美國 Becker 直接發貨， 單次寄送郵資為新台幣 1,200 元  。<br/>
                    </div>
                    <div className="container mb100 mt100">
                        <h2 className="font-weight-bold pb-5 text-center">Becker CPA (美國註冊會計師) 學員加購_線上報名表</h2>
                        <div className="card w-100 p-5 m-auto">
                            <div className="card-body">
                                <p>請完整閱讀此表單，確認內容後填寫並完成繳費。</p>
                                {/* 學員姓名 */}
                                <div className="form-group mb-4">
                                    <label>學員姓名</label>
                                    <input type="text" className="form-control" name="studentName" value={formData.studentName} readOnly />
                                </div>

                                {/* 備註 */}
                                <div className="form-group mb-4">
                                    <label>您的 Becker 軟體帳號（email 全址）*</label>
                                    <input className="form-control" name="account" value={formData.account} onChange={handleChange} />
                                </div>
                                <form onSubmit={handleSubmit}>
                                    {/* 加購項目 */}
                                    <label>選擇加購項目</label>
                                    <div className="checkbox-group mb-5 mt-3">
                                        {groupedAddOnOptions.map(({ groupName, items }) => (
                                            <div key={groupName} className="mb-4">
                                                <h5 className="fw-bold mb-3">{groupName}</h5>

                                                {items.map((item) => (
                                                    <div key={item.id} className="form-check mb-3">
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            value={item.id}
                                                            onChange={handleCheckboxChange}
                                                            checked={formData.add_on_items.some(i => Number(i.id) === Number(item.id))}
                                                        />
                                                        <label className="form-check-label mt-2">
                                                            {item.name} - NT${item.price}
                                                        </label>

                                                        {(() => {
                                                            const iid = Number(item.id);
                                                            const selected = formData.add_on_items.some(i => Number(i.id) === iid);
                                                            if (!QTY_IDS.includes(iid) || !selected) return null;

                                                            return (
                                                                <input
                                                                    type="number"
                                                                    className="form-control mt-2"
                                                                    placeholder="請輸入數量"
                                                                    min={1}
                                                                    value={formData.quantityMap[iid] || 1}
                                                                    onChange={(e) => {
                                                                        const value = parseInt(e.target.value, 10) || 1;
                                                                        const newQuantityMap = {
                                                                            ...formData.quantityMap,
                                                                            [iid]: value
                                                                        };

                                                                        setFormData(prev => ({
                                                                            ...prev,
                                                                            quantityMap: newQuantityMap
                                                                        }));

                                                                        calculateTotal(
                                                                            formData.add_on_items,
                                                                            newQuantityMap,
                                                                            formData.address_type,
                                                                            formData.deliver
                                                                        );
                                                                    }}
                                                                />
                                                            );
                                                        })()}

                                                        {(() => {
                                                            const iid = Number(item.id);
                                                            const selected = formData.add_on_items.some(i => Number(i.id) === iid);
                                                            if (!MEMO_IDS.includes(iid) || !selected) return null;

                                                            return (
                                                                <input
                                                                    type="text"
                                                                    className="form-control mt-2"
                                                                    placeholder="請輸入備註"
                                                                    value={formData.memoMap[iid] || ""}
                                                                    onChange={(e) => {
                                                                        const value = e.target.value;
                                                                        setFormData(prev => ({
                                                                            ...prev,
                                                                            memoMap: { ...prev.memoMap, [iid]: value }
                                                                        }));
                                                                    }}
                                                                />
                                                            );
                                                        })()}
                                                    </div>
                                                ))}
                                            </div>
                                        ))}
                                    </div>
                                    {/* 是否郵寄 */}
                                    <div className="form-group mb-4">
                                        <label>是否需要郵寄？</label>
                                        <select name="deliver" className="form-control" value={formData.deliver} onChange={handleChange}>
                                            <option value="no">否</option>
                                            <option value="yes">是</option>
                                        </select>
                                    </div>
                                    {formData.deliver === "yes" && (
                                        <>
                                            <div className="form-group mb-4">
                                                <label>寄送地區</label>
                                                <select
                                                    name="address_type"
                                                    className="form-control"
                                                    value={formData.address_type}
                                                    onChange={handleChange}
                                                >
                                                    <option value="domestic">台灣國內</option>
                                                    <option value="foreign">海外</option>
                                                </select>
                                            </div>

                                            <div className="form-group mb-4">
                                                <label>郵寄地址</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="address"
                                                    value={formData.address}
                                                    onChange={handleChange}
                                                    required
                                                />
                                            </div>
                                        </>
                                    )}
                                    

                                    {/* 發票類型 */}
                                    <div className="form-group mb-4">
                                        <label>發票類型</label>
                                        <select name="invoice" className="form-control" value={formData.invoice} onChange={handleChange}>
                                            <option value="personal">個人發票</option>
                                            <option value="company">公司發票</option>
                                        </select>
                                    </div>

                                    {/* 公司資訊（僅當選擇公司發票時顯示） */}
                                    {formData.invoice === "company" && (
                                        <>
                                            <div className="form-group mb-4">
                                                <label>公司名稱</label>
                                                <input type="text" className="form-control" name="company_name" value={formData.company_name} onChange={handleChange} />
                                            </div>
                                            <div className="form-group mb-4">
                                                <label>公司統編</label>
                                                <input type="text" className="form-control" name="company_tax" value={formData.company_tax} onChange={handleChange} />
                                            </div>
                                        </>
                                    )}

                                    {/* 匯款資訊 */}
                                    <div className="form-group mb-4">
                                        <label>匯款時間</label>
                                        <input type="datetime-local" className="form-control" name="remit_time" value={formData.remit_time} onChange={handleChange} required />
                                    </div>
                                    <div className="form-group mb-4">
                                        <label>匯款人姓名 / 轉帳帳號</label>
                                        <input type="text" className="form-control" name="remit_name" value={formData.remit_name} onChange={handleChange} required />
                                    </div>
                                    
                                    {formData.deliver === "yes" && shippingInfo.totalBoxes > 0 && (
                                        <div className="alert alert-secondary mb-4">
                                            <div>國內書本數：{shippingInfo.domesticCount} 本（每 2 本 1 箱）</div>
                                            <div>EA 書本數：{shippingInfo.overseasCount} 本（每 4 本 1 箱）</div>
                                            <div>總箱數：{shippingInfo.totalBoxes} 箱</div>
                                            <div>每箱運費：NT${shippingInfo.feePerBox}</div>
                                            <div><strong>書籍運費：NT${shippingInfo.shippingFee}</strong></div>
                                        </div>
                                    )}
                                    {/* 顯示總金額 */}
                                    <div className="form-group mb-4">
                                        <label>總金額 (新台幣)</label>
                                        <input type="text" className="form-control" name="total_amount" value={formData.total_amount} readOnly />
                                    </div>
                                    {/* 確認閱讀條款 */}
                                    <div className="form-group mb-4">
                                        <label>報名與購買前，是否已完整審閱上述購買須知與相關規定？</label>
                                        <div className="form-check">
                                            <input type="radio" className="form-check-input" name="reviewedTerms" value="yes" onChange={handleAgreementChange} />
                                            <label className="form-check-label">是，我已閱讀並同意</label>
                                        </div>
                                        <div className="form-check">
                                            <input type="radio" className="form-check-input" name="reviewedTerms" value="no" onChange={handleAgreementChange} />
                                            <label className="form-check-label">否，我尚未理解</label>
                                        </div>
                                    </div>

                                    {/* 確認理解購買須知 */}
                                    <div className="form-group mb-4">
                                        <label>是否瞭解及接受 Becker 方案購買須知與相關規定？</label>
                                        <div className="form-check">
                                            <input type="radio" className="form-check-input" name="understandPolicy" value="yes" onChange={handleAgreementChange} />
                                            <label className="form-check-label">是，我已詳閱並理解</label>
                                        </div>
                                        <div className="form-check">
                                            <input type="radio" className="form-check-input" name="understandPolicy" value="no" onChange={handleAgreementChange} />
                                            <label className="form-check-label">否，我尚未理解</label>
                                        </div>
                                    </div>
                                   {/* 確認接受條款 */}
                                    <div className="form-group mb-4">
                                        <label>是否接受所有購買與退費相關條款？</label>
                                        <div className="form-check">
                                            <input type="radio" className="form-check-input" name="acceptTerms" value="yes" onChange={handleAgreementChange} />
                                            <label className="form-check-label">是，我接受所有條款</label>
                                        </div>
                                        <div className="form-check">
                                            <input type="radio" className="form-check-input" name="acceptTerms" value="no" onChange={handleAgreementChange} />
                                            <label className="form-check-label">否，我尚未接受</label>
                                        </div>
                                    </div>
                                    

                                    {/* 提交按鈕 */}
                                    <button type="submit" className="btn btn-primary w-100" disabled={isSubmitting}>
                                        {isSubmitting ? "提交中..." : "提交申請"}
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AddRequestForm;
